/**
 * Child Dashboard
 * Main dashboard for children with subject selection and learning progress
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Sparkles } from 'lucide-react';
import { SUBJECT_DISPLAY_NAMES, SUBJECT_ICONS } from '@kids-lms/constants';
import type { Subject } from '@kids-lms/types';
import { useChildStore, useSoundEffects, useHaptic, useReducedMotion } from '../hooks';
import { useTimeOfDay } from '../hooks/useTimeOfDay';
import MindoraCharacter from '../components/character/MindoraCharacter';
import StarBurst from '../components/effects/StarBurst';
import { SparkleCluster, SoftShape, WavyDivider, DecorativeStar } from '../components/decorations';
import SpeechBubble from '../components/ui/SpeechBubble';
import AnimatedButton from '../components/ui/AnimatedButton';
import { StaggerList, StaggerItem } from '../components/ui/StaggerList';
import DailyCheckIn from '../components/DailyCheckIn';
import CelebrationOverlay from '../components/CelebrationOverlay';

// Saturated gradient configs — white text, kid-palette shadows
const subjectConfig: Record<Subject, { gradient: string; shadow: string; bgImage?: string }> = {
  math:      { gradient: 'from-kid-ocean to-kid-sky',          shadow: 'shadow-kid',    bgImage: '/math_bg.png' },
  science:   { gradient: 'from-kid-meadow to-kid-ocean',       shadow: 'shadow-kid',    bgImage: '/science_bg.png' },
  english:   { gradient: 'from-kid-violet to-kid-berry',       shadow: 'shadow-kid',    bgImage: '/english_bg.png' },
  history:   { gradient: 'from-kid-dandelion to-kid-tangerine', shadow: 'shadow-kid-warm' },
  geography: { gradient: 'from-kid-ocean to-kid-meadow',       shadow: 'shadow-kid' },
  art:       { gradient: 'from-kid-berry to-kid-bubblegum',    shadow: 'shadow-kid' },
  music:     { gradient: 'from-kid-violet to-kid-sky',         shadow: 'shadow-kid' },
  coding:    { gradient: 'from-kid-tangerine to-kid-coral',    shadow: 'shadow-kid-warm', bgImage: '/codding_bg.png' },
  general:   { gradient: 'from-kid-sky to-kid-ocean',          shadow: 'shadow-kid' },
};

/** Growing plant streak visualization — grows taller with more days */
function StreakPlant({ streak }: { streak: number }) {
  // Plant stages: seed (0), sprout (1-2), small (3-6), medium (7-13), tall (14-29), tree (30+)
  const stage = streak === 0 ? 0 : streak <= 2 ? 1 : streak <= 6 ? 2 : streak <= 13 ? 3 : streak <= 29 ? 4 : 5;
  const height = [8, 16, 24, 32, 40, 48][stage];
  const leaves = Math.min(stage, 4);

  return (
    <svg width="32" height="52" viewBox="0 0 32 52" className="block" role="img" aria-label={`${streak} day streak plant`}>
      {/* Pot */}
      <rect x="8" y="44" width="16" height="8" rx="2" fill="var(--kid-tangerine)" opacity={0.3} />
      {/* Stem */}
      <motion.rect
        x="15"
        width="2"
        rx="1"
        fill="var(--kid-meadow)"
        initial={{ height: 0, y: 44 }}
        animate={{ height, y: 44 - height }}
        transition={{ type: 'spring', stiffness: 100, damping: 15, delay: 0.3 }}
      />
      {/* Leaves */}
      {leaves >= 1 && (
        <motion.ellipse cx="20" cy={44 - height * 0.4} rx="5" ry="3" fill="var(--kid-meadow)"
          initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5 }} />
      )}
      {leaves >= 2 && (
        <motion.ellipse cx="12" cy={44 - height * 0.6} rx="5" ry="3" fill="var(--kid-meadow)"
          initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6 }} />
      )}
      {leaves >= 3 && (
        <motion.ellipse cx="20" cy={44 - height * 0.8} rx="4" ry="2.5" fill="var(--kid-meadow)"
          initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.7 }} />
      )}
      {/* Flower at top for 7+ days */}
      {stage >= 3 && (
        <motion.circle cx="16" cy={44 - height - 3} r="4" fill="var(--kid-berry)"
          initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.8, type: 'spring' }} />
      )}
      {/* Star for 30+ days */}
      {stage >= 5 && (
        <motion.text x="12" y={44 - height - 8} fontSize="10"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
          ⭐
        </motion.text>
      )}
    </svg>
  );
}

export default function ChildDashboard() {
  const navigate = useNavigate();
  const {
    selectedChild,
    showCelebration,
    setShowCelebration,
    streakData,
    fetchHabits,
  } = useChildStore();
  const { play } = useSoundEffects();
  const haptic = useHaptic();
  const reduced = useReducedMotion();
  const timeOfDay = useTimeOfDay();
  const [sessionBurst, setSessionBurst] = useState(false);
  const [companionMood, setCompanionMood] = useState<'waving' | 'celebrating' | 'happy'>('waving');
  const tapCountRef = useRef(0);
  const tapResetRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const handleCloseCelebration = useCallback(() => setShowCelebration(false), [setShowCelebration]);

  // Easter egg: tap companion 5 times → silly dance
  const handleCompanionTap = useCallback(() => {
    tapCountRef.current += 1;
    clearTimeout(tapResetRef.current);
    tapResetRef.current = setTimeout(() => { tapCountRef.current = 0; }, 2000);
    if (tapCountRef.current >= 5) {
      tapCountRef.current = 0;
      setCompanionMood('celebrating');
      play('celebratoryWoohoo');
      haptic.celebrate();
      setSessionBurst(true);
      setTimeout(() => { setSessionBurst(false); setCompanionMood('happy'); }, 2000);
      setTimeout(() => setCompanionMood('waving'), 4000);
    } else {
      play('tap');
      haptic.tap();
    }
  }, [play, haptic]);

  // Session start celebration
  useEffect(() => {
    setSessionBurst(true);
    const t = setTimeout(() => setSessionBurst(false), 1000);
    return () => clearTimeout(t);
  }, []);

  // Fetch habits on mount
  useEffect(() => {
    if (selectedChild?.id) {
      fetchHabits(selectedChild.id);
    }
  }, [selectedChild?.id, fetchHabits]);

  const handleSubjectClick = (subject: Subject) => {
    navigate(`/learn/chat?subject=${subject}`);
  };

  const handleSubjectPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    haptic.tap();
    play('tap');
    // Set glow coordinates
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--touch-x', `${((e.clientX - rect.left) / rect.width) * 100}%`);
    e.currentTarget.style.setProperty('--touch-y', `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  const subjects: Subject[] = ['math', 'science', 'english', 'history', 'art', 'coding', 'general'];

  return (
    <div className="min-h-screen bg-kid-bg relative">
      <CelebrationOverlay show={showCelebration} onClose={handleCloseCelebration} />

      {/* Background blobs */}
      <SoftShape color="var(--kid-violet)" size={250} variant="blob1" className="absolute top-20 -left-20 opacity-10" />
      <SoftShape color="var(--kid-ocean)" size={200} variant="blob2" className="absolute top-[60%] -right-16 opacity-10" />
      <SoftShape color="var(--kid-berry)" size={180} variant="blob3" className="absolute bottom-40 left-[10%] opacity-10" />

      {/* Hero Banner */}
      <StaggerItem className="relative overflow-hidden">
        {/* Sparkle cluster near hero */}
        <SparkleCluster count={5} spread={50} className="absolute top-4 right-8 z-10" />
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="relative rounded-kid overflow-hidden shadow-kid-lg border-4 border-white/50">
            <img
              src="/banner2.png"
              alt="Mindora - Learn with Wonder"
              className="w-full h-48 sm:h-64 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-kid-ocean/80 to-transparent flex items-center">
              <div className="p-6 sm:p-10 text-white flex items-center gap-4">
                <div className="relative cursor-pointer" onClick={handleCompanionTap} role="button" aria-label="Tap Mindora companion">
                  <MindoraCharacter mood={companionMood} size="lg" />
                  <StarBurst trigger={sessionBurst} />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-4xl font-display mb-2">
                    {selectedChild
                      ? `${timeOfDay === 'morning' ? 'Good morning' : timeOfDay === 'afternoon' ? 'Hey there' : 'Good evening'}, ${selectedChild.name}!`
                      : 'Welcome to Mindora!'}
                  </h1>
                  <p className="text-white/90 text-kid-base max-w-md">
                    {timeOfDay === 'morning'
                      ? 'Ready to start a new learning adventure?'
                      : timeOfDay === 'afternoon'
                      ? 'Ready for another learning adventure?'
                      : 'Time for one more learning adventure before bed?'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StaggerItem>

      <StaggerList stagger="slow" className="p-6 max-w-6xl mx-auto">
        {/* Streak Visualization — growing plant metaphor */}
        <StaggerItem>
          <div className="flex gap-4 mb-6 justify-center items-end">
            <div className="bg-white rounded-kid px-5 py-3 shadow-kid border-2 border-kid-tangerine/20 text-center relative overflow-hidden">
              {/* Growing plant visual for streak */}
              <div className="flex justify-center mb-1">
                <StreakPlant streak={streakData?.habitStreak || 0} />
              </div>
              <span className="text-2xl font-display text-kid-tangerine block">{streakData?.habitStreak || 0}</span>
              <p className="text-kid-xs text-kid-text-soft font-bold">🔥 Streak</p>
              {(streakData?.habitStreak || 0) > 0 && (
                <p className="text-[9px] text-kid-text-soft font-kid mt-0.5">
                  Best: {Math.max(streakData?.habitStreak || 0, streakData?.habitStreak || 0)}
                </p>
              )}
            </div>
            <div className="bg-white rounded-kid px-5 py-3 shadow-kid border-2 border-kid-dandelion/20 text-center">
              <span className="text-2xl font-display text-kid-dandelion block">{streakData?.habitPoints || 0}</span>
              <p className="text-kid-xs text-kid-text-soft font-bold">⭐ Points</p>
            </div>
          </div>
        </StaggerItem>

        {/* Daily Check-In — simple toggles */}
        <StaggerItem className="mb-6">
          <DailyCheckIn />
        </StaggerItem>

        {/* Question Section */}
        <StaggerItem className="text-center mb-8 relative">
          <DecorativeStar size={20} pulse className="absolute -top-2 left-[20%]" />
          <DecorativeStar size={14} pulse className="absolute top-1 right-[18%]" color="#9775FA" />
          <h2 className="text-kid-2xl font-display text-kid-text mb-2">
            What do you want to learn today?
          </h2>
          <p className="text-kid-text-soft text-kid-base">
            Pick a subject and start exploring!
          </p>
        </StaggerItem>

        {/* Subject Grid — saturated gradient cards */}
        <StaggerItem>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {subjects.map((subject, index) => {
            const config = subjectConfig[subject];
            return (
              <motion.button
                key={subject}
                whileHover={{ scale: 1.08, y: -8, rotate: index % 2 === 0 ? 2 : -2 }}
                whileTap={{ scale: 0.92 }}
                onClick={() => handleSubjectClick(subject)}
                onPointerDown={handleSubjectPointerDown}
                aria-label={`Learn ${SUBJECT_DISPLAY_NAMES[subject]}`}
                className={`relative overflow-hidden rounded-kid shadow-lg hover:shadow-xl transition-all duration-300 touch-glow ${
                  config.bgImage ? '' : `bg-gradient-to-br ${config.gradient} ${config.shadow}`
                } border-4 border-white/30 wobble-${(index % 4) + 1}`}
              >
                {config.bgImage ? (
                  <>
                    <img
                      src={config.bgImage}
                      alt={SUBJECT_DISPLAY_NAMES[subject]}
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-3 bg-white/95">
                      <span className="text-2xl block mb-1">{SUBJECT_ICONS[subject]}</span>
                      <h3 className="font-display text-kid-text text-center text-kid-sm">
                        {SUBJECT_DISPLAY_NAMES[subject]}
                      </h3>
                    </div>
                  </>
                ) : (
                  <div className="p-6 text-white">
                    <span className="text-5xl mb-3 block">{SUBJECT_ICONS[subject]}</span>
                    <h3 className="font-display text-center text-kid-base">
                      {SUBJECT_DISPLAY_NAMES[subject]}
                    </h3>
                    {/* Sparkle decorations */}
                    {!reduced && (
                      <motion.div
                        className="absolute top-2 right-2 opacity-60"
                        aria-hidden="true"
                        animate={{ rotate: 360, scale: [0.8, 1.2, 0.8] }}
                        transition={{ repeat: Infinity, duration: 3, delay: index * 0.3 }}
                      >
                        <Sparkles className="w-5 h-5 text-white/80" />
                      </motion.div>
                    )}
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>
        </StaggerItem>

        {/* Wavy divider */}
        <WavyDivider className="my-8" />

        {/* Ask Anything Card */}
        <StaggerItem className="mt-2">
          <div className="bg-gradient-to-r from-kid-ocean via-kid-violet to-kid-berry rounded-kid p-8 text-white shadow-kid-lg border-4 border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-kid-sm flex items-center justify-center">
                  <MessageCircle className="w-8 h-8" strokeWidth={2.25} />
                </div>
                <div>
                  <h3 className="text-kid-xl font-display">Have a question?</h3>
                  <p className="text-white/80 text-kid-base">Ask me anything you want to know!</p>
                </div>
              </div>
              <AnimatedButton
                variant="marshmallow"
                onClick={() => navigate('/learn/chat')}
                className="btn-marshmallow-violet bg-white text-kid-violet font-display px-8 py-4 rounded-kid-sm hover:bg-kid-bg transition-colors shadow-kid-md whitespace-nowrap text-kid-base"
              >
                <Sparkles className="inline w-5 h-5 mr-2" />
                Start Chatting!
              </AnimatedButton>
            </div>
          </div>
        </StaggerItem>

        {/* Wavy divider */}
        <WavyDivider className="my-6" color="var(--kid-dandelion)" />

        {/* Learning Tips — mascot speech bubble (rotates daily) */}
        <StaggerItem className="mt-4">
          <div className="flex items-start gap-4">
            <MindoraCharacter mood="encouraging" size="md" />
            <SpeechBubble direction="left" className="flex-1">
              <h3 className="font-display text-kid-text text-kid-lg mb-2 flex items-center gap-2">
                <span className="text-2xl" aria-hidden="true">💡</span> Learning Tip of the Day
              </h3>
              <p className="text-kid-text-soft text-kid-base">
                {[
                  'Take breaks every 30 minutes! Your brain learns better when you give it time to rest. Try stretching or getting a drink of water!',
                  'Teaching someone else what you learned is one of the best ways to remember it! Try explaining a fun fact to your family.',
                  'Drawing pictures of what you learn helps your brain remember it better. Try sketching your favorite lesson!',
                  'Reading before bed is like a superpower — your brain keeps learning while you sleep!',
                  'Asking "why?" is what scientists do! The more questions you ask, the smarter you get.',
                  'Making mistakes is how your brain grows! Every wrong answer teaches you something new.',
                  'Your brain is like a muscle — the more you use it, the stronger it gets!',
                ][Math.floor(Date.now() / 86400000) % 7]}
              </p>
            </SpeechBubble>
          </div>
        </StaggerItem>

        {/* Wavy divider */}
        <WavyDivider className="my-6" color="var(--kid-violet)" />

        {/* Fun Facts Section */}
        <StaggerItem className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { emoji: '🌟', text: 'Did you know? Octopuses have three hearts!', color: 'kid-meadow' },
            { emoji: '🚀', text: 'A day on Venus is longer than a year on Venus!', color: 'kid-sky' },
            { emoji: '🦋', text: 'Butterflies taste with their feet!', color: 'kid-violet' },
          ].map((fact, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.03 }}
              className={`bg-white rounded-kid p-5 border-2 border-kid-ocean/15 shadow-kid hover-wobble wobble-${(i % 4) + 1}`}
            >
              <span className="text-4xl block mb-2">{fact.emoji}</span>
              <p className="text-kid-text-soft text-kid-sm">{fact.text}</p>
            </motion.div>
          ))}
        </StaggerItem>
      </StaggerList>
    </div>
  );
}
